// Class-index → label maps for the M1 multiclass heads. Fixed by the trained
// model and NOT carried in the prediction row. Source of truth upstream is
// jedi-archive/training/configs/head_configs.yaml + shared/labels/derive.py —
// ping jedi-archive if these drift.

export const CAREER_TRAJECTORY_LABELS = ["Regressing", "Stable", "Lateral", "Rising"];

// skip_risk was de-listed (no longer served) in the 2026-06-13 M1 deploy, so it
// is no longer rendered on the intelligence cards. This class-index → label map
// is retained only because the Mavi audience filter (FiltersDrawer +
// maviFilterParams → backend maviFilter.ts / MOFU drip) still exposes it; that
// filter chain spans the backend and is a separate removal from the cards.
export const SKIP_RISK_LABELS = [
    "Wrong skills",
    "Wrong level",
    "Wrong location",
    "Wrong rate",
    "Wrong engagement",
    "Already engaged",
    "Communication",
    "Profile quality",
    "Other",
    "None",
];

// Human-friendly head names (modal headers, model-details footer, tooltips).
export const HEAD_DISPLAY_NAMES: Record<string, string> = {
    admit: "Approve Application",
    fast_track: "Fast track",
    level: "Level",
    junior_gate: "Junior gate",
    senior_gate: "Senior gate",
    network_admission: "Network admission",
    shortlist_likelihood: "Shortlist likelihood",
    career_trajectory: "Career trajectory",
    abstain: "Abstain",
};

// One-line plain-language interpretation per head (§2.6). {label}/{n} are
// filled by the renderer where applicable.
export const HEAD_INTERPRETATIONS: Record<string, string> = {
    admit: "Likelihood of clearing application review",
    fast_track: "Likelihood of skipping straight to a live interview",
    junior_gate: "Junior pool (L1–L3) vs senior pool",
    senior_gate: "Senior pool (L4+) vs junior pool",
    network_admission: "Likelihood of being admitted to the network",
    shortlist_likelihood: "Likelihood a recruiter shortlists them",
};

// Fields that carry the single calibrated probability for each binary head.
export const BINARY_HEAD_PROB_FIELD: Record<string, string> = {
    admit: "score",
    fast_track: "pass_prob",
    junior_gate: "is_junior_prob",
    senior_gate: "is_senior_prob",
    network_admission: "prob",
    shortlist_likelihood: "prob",
};

export function headDisplayName(head: string): string {
    return HEAD_DISPLAY_NAMES[head] ?? head;
}

// ── Per-head trust (how much to believe each head) ───────────────────
// Sourced from jedi-archive's training analysis. UPDATED 2026-06-16 after the M1
// LEAKAGE-REMEDIATION redeploy (image 166a129 / models 2026-06-16-469b388). These
// are HONEST holdout numbers and are deliberately LOWER than the 2026-06-15 set —
// jedi-archive found systemic point-in-time leakage: candidate child-table features
// (work_experience, education, tag, certification) were read without a row-creation
// (created_at) bound, so post-application edits + records entered during review
// (certs were created a median 160 DAYS after application) leaked into training and
// inflated every prior holdout metric. The created_at guards remove it; the drops
// below are the cost of honesty, not a regression. Net moves vs 2026-06-15:
// network_admission 0.94/0.92→0.88/0.85 (cleanest leakage signal), career
// 88%/77%→78%/73%, level acct exact 54%→46%, admit acct AUC 0.79→0.77, junior_gate
// acct 0.81→0.72 (now below the 0.80 gate). The DATA-STARVED heads degraded to
// unreliable on honest features (junior_gate FP&A AUC 0.48 < random, level FP&A,
// shortlist n=30) — they remain served (shadow, low-trust) ONLY to keep collecting
// thumbs feedback that will improve them; do not rely on their scores. Backfilled
// back-catalog scores are not yet point-in-time (jedi P1-057). Binary heads report
// holdout AUC; level/career report exact (±1 for level). Update on the next reship.
//
// UPDATED 2026-06-24 — M1 LEVEL-FAMILY reship (models 2026-06-23-c9fad83, P1-058).
// Only level + junior_gate + senior_gate were retrained; admit / fast_track /
// network_admission / career_trajectory / shortlist_likelihood are UNCHANGED from the
// 2026-06-16 469b388 set (their entries below keep that date). Changes:
//   • fix: level no longer trains on serve-time-skewed features (cand_ls_age_days,
//     language_proficiency) — they inflated the holdout (73%) but vanish at serving.
//   • junior_gate jumped 0.72→0.91 (Acc) / 0.48→0.92 (FP&A): the labelled cohort grew
//     ~10x (a 2026-04/05 application surge) so it is no longer data-starved, and it is
//     leakage-trimmed (--min-application-date 2025-12-03). senior_gate (NEW, L4+) shares
//     the Staff/Senior boundary → identical AUC.
//   • level exact rose to 58%/64% (±1 96%/98%) — exact is still capped by within-band
//     L1-L7 ambiguity (a coin-flip even with interview signal); ±1 is the reliable read.
//     Higher exact needs a labelling change (jedi-archive P1-059).
//   FAIRNESS (2026-06-24, P1-060): geography proxies were dropped from ALL heads
//   (country/region/preferred-hours) at <1pp AUC cost. A per-country skew remains
//   (e.g. FP&A Chile +25pp senior), BUT the label-vs-model analysis proved the model is
//   FAITHFUL — the gaps live in the recruiter LABELS (or real pool differences, e.g.
//   strong local finance sectors), not introduced by the model (it even dampens them).
//   So the gates are no less fair than the human leveling they replicate; model debiasing
//   is NOT the fix. Open question: are the labels themselves fair? → recruiter-lead review
//   (P1-059). junior_gate / senior_gate / admit stay SHADOW-ONLY pending that review.
export const HEAD_TRUST_AS_OF = "2026-06-24";

export type TrustTier = "high" | "medium" | "low";

export interface HeadTrust {
    /** Short pill text, e.g. "AUC 0.81" / "Exact 88%". */
    metric: string;
    tier: TrustTier;
    /** Full hover detail. */
    detail: string;
}

const t = (metric: string, tier: TrustTier, detail: string): HeadTrust => ({ metric, tier, detail });

// Keyed by head, then by scoring_role. A missing (head, role) entry means we
// have no trained model / published metric for that pairing.
export const HEAD_TRUST: Record<string, Partial<Record<string, HeadTrust>>> = {
    admit: {
        Accounting: t("AUC 0.77", "medium", "Accounting · AUC 0.774 (honest, leakage-corrected) — band-aware calibrated + de-saturated (2026-06-16)"),
        "FP&A": t("AUC 0.65", "low", "FP&A · AUC 0.650 — weak; defer to the lead score (D26)"),
    },
    fast_track: {
        Accounting: t("AUC 0.96", "high", "Accounting · AUC 0.957 — rubric-driven, unaffected by the leakage fix (2026-06-16)"),
        "FP&A": t("AUC 0.94", "high", "FP&A · AUC 0.943 (2026-06-16)"),
    },
    junior_gate: {
        Accounting: t("AUC 0.90", "medium", "Accounting · AUC 0.899 — geo-proxy-free (fairness fix 9d071bf), leakage-trimmed, well-calibrated. Residual per-country bias remains (P1-060) → SHADOW-ONLY, don't route (2026-06-24)"),
        "FP&A": t("AUC 0.91", "medium", "FP&A · AUC 0.914 — geo-proxy-free, well-calibrated. Residual per-country bias (Chile −26%, P1-060) → SHADOW-ONLY (2026-06-24)"),
    },
    // senior_gate (NEW 2026-06-24) — L4+ complement of junior_gate at the same
    // Staff/Senior boundary, so its AUC is identical. Exposed because the product
    // uses both the "is junior" and "is senior" framings.
    senior_gate: {
        Accounting: t("AUC 0.90", "medium", "Accounting · AUC 0.899 — L4+ gate, geo-proxy-free (fairness fix 9d071bf). Residual per-country bias (P1-060) → SHADOW-ONLY (2026-06-24)"),
        "FP&A": t("AUC 0.91", "medium", "FP&A · AUC 0.914 — L4+ gate, geo-proxy-free. Residual per-country bias (Chile +25%, P1-060) → SHADOW-ONLY (2026-06-24)"),
    },
    network_admission: {
        Accounting: t("AUC 0.88", "high", "Accounting · AUC 0.879 (honest) — dropped from 0.94 when post-decision profile-completeness leakage was removed; shadow-only (2026-06-16)"),
        "FP&A": t("AUC 0.85", "high", "FP&A · AUC 0.850 (honest) — dropped from 0.92 (leakage-corrected); shadow-only (2026-06-16)"),
    },
    shortlist_likelihood: {
        Accounting: t("AUC 0.58", "low", "Accounting · AUC 0.578 — DATA-STARVED (holdout n=30, not validatable); served only to collect feedback (2026-06-16)"),
    },
    level: {
        Accounting: t("±1 96%", "medium", "Accounting · exact ~58%, within ±1 ~96% — serve-skew + geo-proxy-free (9d071bf; was 46%/83%). Exact is capped by within-band L1-L7 ambiguity; ±1 is the reliable read (2026-06-24)"),
        "FP&A": t("±1 98%", "medium", "FP&A · exact ~63%, within ±1 ~98% — geo-proxy-free, no longer data-starved (2026-06-24)"),
    },
    career_trajectory: {
        Accounting: t("Exact 78%", "medium", "Accounting · exact ~78% (honest; was ~88% leakage-inflated) (2026-06-16)"),
        "FP&A": t("Exact 73%", "medium", "FP&A · exact ~73% (honest; was ~77%) (2026-06-16)"),
    },
};

export function getHeadTrust(head: string, scoringRole: string | null | undefined): HeadTrust | undefined {
    if (!scoringRole) return undefined;
    return HEAD_TRUST[head]?.[scoringRole];
}

// ── M2 interview model (shadow + Accounting auto-route pilot) ────────
// jedi-archive's M2 interview model writes per-attempt predictions to citadel
// ml.interview_predictions in shadow mode. The Technical Interview modal shows
// these as an advisory signal. ONE exception since 2026-07-23: the Accounting
// auto-route pilot — organic manual-review candidates whose technical_pass
// decision is auto_approve are promoted automatically by interview-lab
// (labeled 'M2-Auto-Route'). Every other head/role is advisory only.
// Heads: technical_pass | level | strength_tier | comms_readiness.

export const M2_HEAD_DISPLAY_NAMES: Record<string, string> = {
    technical_pass: "Technical Pass",
    level: "Predicted Level",
    strength_tier: "Strength",
    comms_readiness: "Comms Readiness",
};

// strength_tier.predicted_class → label.
export const STRENGTH_TIER_LABELS: Record<number, string> = {
    0: "Bottom",
    1: "Mid",
    2: "Top",
};

// technical_pass.decision → display label + palette. Reuses the M1 tier colors.
export const M2_DECISION_META: Record<string, { label: string; bg: string; fg: string }> = {
    auto_approve: { label: "Auto-approve", bg: "rgba(34,197,94,0.14)", fg: "#166534" },
    manual_review: { label: "Manual review", bg: "rgba(245,158,11,0.16)", fg: "#92400E" },
    auto_reject: { label: "Auto-reject", bg: "rgba(239,68,68,0.14)", fg: "#991B1B" },
};

// Per-head trust for M2 — sourced from jedi-archive
// UPDATED 2026-08-12 after the P2-007 step-1 retrain deployed (artifacts
// 2026-08-12-234bfc2, window 2026-03-01→06-15, matured 56d-censored labels).
// Why the retrain: the technical pass rate HALVED Jan→Jun (40%→5.7% Acct), so
// the old thresholds were fit on a world that no longer exists, and the old
// strength_tier had collapsed on post-June attempts (tier-top recall 3–8% —
// prompt-version feature drift v3→v10). New serving thresholds: Accounting
// lo=0.0753/hi=0.5556, FP&A lo=0.0593/hi=0.4916 (the FP&A manual band is now
// REAL — the P2-006 "degenerate hi" caveat is resolved). ⚠ NOT purely shadow
// anymore: since 2026-07-23 the Accounting AUTO-ROUTE PILOT promotes organic
// manual-review candidates when technical_pass says auto_approve
// (interview-lab M2_AUTOROUTE_ACCOUNTING; profiles.metadata.approved
// .approved_by='M2-Auto-Route'). All other heads/roles remain advisory.
// Numbers: jedi-archive tasks/outputs/M2-retrain-step1-2026-08-12.md +
// M2-autoroute-impact-2026-08-12.md; update on the next M2 reship (~09-14
// recalibration on matured v7–v10 labels).
export const M2_HEAD_TRUST_AS_OF = "2026-08-12";

export interface M2HeadTrust extends HeadTrust {
    shadow: true;
}

const m2 = (metric: string, tier: TrustTier, detail: string): M2HeadTrust => ({
    metric,
    tier,
    detail,
    shadow: true,
});

export const M2_HEAD_TRUST: Record<string, Partial<Record<string, M2HeadTrust>>> = {
    technical_pass: {
        Accounting: m2("AUC 0.98", "high", "Accounting · holdout AUC 0.976 on matured labels, thresholds lo=0.075/hi=0.556 (2026-08-12). ⚠ Drives the live auto-route pilot: organic manual-review candidates with auto_approve are promoted automatically (since 07-23)"),
        "FP&A": m2("AUC 0.99", "high", "FP&A · holdout AUC 0.992 on matured labels, thresholds lo=0.059/hi=0.492 — the manual band is now real (old degenerate-hi resolved, 2026-08-12); shadow"),
    },
    level: {
        Accounting: m2("±1 98%", "medium", "Accounting · exact ~76%, within ±1 ~98% (2026-08-12, shadow)"),
        "FP&A": m2("±1 99%", "medium", "FP&A · exact ~77%, within ±1 ~99% (2026-08-12, shadow)"),
    },
    strength_tier: {
        Accounting: m2("Exact 86%", "medium", "Accounting · exact ~86%, macro-F1 0.58 (2026-08-12, shadow). Retrained after the prior artifact collapsed on post-June attempts (prompt-version drift) — pre-08-12 tier values are unreliable"),
        "FP&A": m2("Exact 80%", "medium", "FP&A · exact ~80%, macro-F1 0.60 (2026-08-12, shadow). Same retrain note as Accounting"),
    },
    comms_readiness: {
        Accounting: m2("Rule ≈ chance", "low", "Deterministic rule comms-rule-v1 — measured ≈ chance (AUC 0.52) at predicting the behavioral round it is named for (jedi 07-23 review). Presentational only; never use for decisions. Learned replacement is P2-016"),
        "FP&A": m2("Rule ≈ chance", "low", "Deterministic rule comms-rule-v1 — measured ≈ chance (AUC 0.58) vs its behavioral target (jedi 07-23 review). Presentational only; never use for decisions. Learned replacement is P2-016"),
    },
};

export function getM2HeadTrust(head: string, scoringRole: string | null | undefined): M2HeadTrust | undefined {
    if (!scoringRole) return undefined;
    return M2_HEAD_TRUST[head]?.[scoringRole];
}

export function m2HeadDisplayName(head: string): string {
    return M2_HEAD_DISPLAY_NAMES[head] ?? head;
}
